import { MutationCache, QueryClient } from "@tanstack/react-query";
import { toast } from "bizpandit-ui";
import { isApiError } from "@/api/types";

/**
 * Central React Query client.
 * - Query errors surface via toast unless the query opts out with meta.silent.
 * - Mutation errors are handled per-call (see feature hooks) so we don't
 *   double-toast; the global handler here is a safety net only.
 */
/**
 * The dashboard reflects every change the moment it is made — QA row 56
 * (BP-ISS-20260916-052, 2026-09-16): a task marked completed from the
 * dashboard kept showing its old status until the page was reloaded, because
 * `/home` is one composed query (`["home"]`, 30 s stale) that no dialog knew
 * to invalidate. Rather than a list in every dialog that the next dialog
 * forgets, every SUCCESSFUL mutation anywhere marks the composed views stale
 * here; only the ones currently on screen refetch, so the cost is one request
 * for the page the person is looking at.
 */
const COMPOSED_VIEW_KEYS: ReadonlyArray<readonly string[]> = [["home"], ["dashboards"], ["tasks"]];

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      for (const queryKey of COMPOSED_VIEW_KEYS) void queryClient.invalidateQueries({ queryKey: [...queryKey] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const status = isApiError(error) ? error.status : undefined;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
      onError: (error) => {
        const message = isApiError(error) ? error.message : error.message;
        toast.error(message ?? "Something went wrong. Please try again.");
      },
    },
  },
});

export const queryKeys = {
  auth: {
    me: ["auth", "me"] as const,
  },
  dashboard: {
    stats: ["dashboard", "stats"] as const,
    revenue: (range: string) => ["dashboard", "revenue", range] as const,
    activity: ["dashboard", "activity"] as const,
  },
} as const;
